import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { uploadMedia } from '@/lib/cloudflare';
import type { Post, ReactionType } from '@/types/database';
import { Heart, MessageCircle, Share2, Image, Video, Flag, X, Trash2 } from 'lucide-react';
import { cn, extractVideoUrl, getVideoPlatform } from '@/lib/utils';
import CommentSection from './CommentSection';
import { ShareMenu } from './ShareMenu';
import { ReportDialog } from './ReportDialog';

const REACTIONS = [
  { type: 'like', emoji: '👍', label: 'Like' },
  { type: 'love', emoji: '❤️', label: 'Love' },
  { type: 'celebrate', emoji: '🎉', label: 'Celebrate' },
  { type: 'support', emoji: '🙌', label: 'Support' },
  { type: 'laugh', emoji: '😂', label: 'Laugh' },
  { type: 'wow', emoji: '😮', label: 'Wow' },
  { type: 'sad', emoji: '😢', label: 'Sad' },
  { type: 'angry', emoji: '😠', label: 'Angry' },
  { type: 'care', emoji: '🤗', label: 'Care' },
] as const;

export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showComments, setShowComments] = useState<Record<string, boolean>>({});
  const [showReactions, setShowReactions] = useState<Record<string, boolean>>({});
  const [showShare, setShowShare] = useState<Record<string, boolean>>({});
  const [showReportDialog, setShowReportDialog] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});

  interface MediaFile extends File {
    type: string;
  }

  useEffect(() => {
    if (user) {
      loadProfile();
      fetchPosts();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('avatar_url')
        .eq('id', user?.id)
        .single();

      if (profile?.avatar_url) {
        setUserAvatar(profile.avatar_url);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const { data: posts, error: fetchError } = await supabase
        .from('posts')
        .select(`
          *,
          user:profiles(*),
          comments:comments(
            *,
            user:profiles(*)
          ),
          user_reaction:reactions(*)
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setPosts(posts || []);
    } catch (error) {
      console.error('Error loading posts:', error);
      setError('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = (id: string) => {
    setImageLoadErrors(prev => ({ ...prev, [id]: true }));
  };

  const getAvatarUrl = (user: any) => {
    if (imageLoadErrors[user.id]) {
      return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.full_name)}`;
    }
    return user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user.full_name)}`;
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] as MediaFile;
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const validVideoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
      const validTypes = [...validImageTypes, ...validVideoTypes];

      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image or video file');
        return;
      }

      // Validate file size (10MB for images, 100MB for videos)
      const maxSize = file.type.startsWith('video/') ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`File size must be less than ${file.type.startsWith('video/') ? '100MB' : '10MB'}`);
        return;
      }

      setSelectedMedia(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const removeMedia = () => {
    setSelectedMedia(null);
    setMediaPreview(null);
  };

  const handleSubmit = async () => {
    if ((!content.trim() && !selectedMedia) || !user) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      let mediaUrl;
      let mediaType;

      // Check for video URLs in content
      const videoUrl = extractVideoUrl(content);
      if (videoUrl) {
        mediaUrl = videoUrl;
        mediaType = 'video';
      } else if (selectedMedia) {
        try {
          mediaUrl = await uploadMedia(selectedMedia);
          mediaType = selectedMedia.type.startsWith('video/') ? 'video' : 'image';
        } catch (error) {
          console.error('Media upload error:', error);
          setError('Failed to upload media. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      const { data: post, error: postError } = await supabase
        .from('posts')
        .insert({
          content: content.trim(),
          user_id: user.id,
          media_url: mediaUrl,
          media_type: mediaType
        })
        .select(`
          *,
          user:profiles(*),
          comments:comments(
            *,
            user:profiles(*)
          ),
          user_reaction:reactions(*)
        `)
        .single();

      if (postError) throw postError;

      setPosts(prev => [post, ...prev]);
      setContent('');
      setSelectedMedia(null);
      setMediaPreview(null);
    } catch (error) {
      console.error('Error creating post:', error);
      setError('Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleReaction = async (postId: string, type: ReactionType) => {
    if (!user) return;

    try {
      const existingReaction = posts
        .find(p => p.id === postId)
        ?.user_reaction?.[0];

      if (existingReaction?.type === type) {
        const { error } = await supabase
          .from('reactions')
          .delete()
          .eq('id', existingReaction.id);

        if (error) throw error;
      } else if (existingReaction) {
        const { error } = await supabase
          .from('reactions')
          .update({ type })
          .eq('id', existingReaction.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('reactions')
          .insert({
            post_id: postId,
            user_id: user.id,
            type
          });

        if (error) throw error;
      }

      await fetchPosts();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <img
              src={userAvatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(user?.email || '')}`}
              alt="Your avatar"
              className="w-10 h-10 rounded-full ring-2 ring-royal-purple/20"
              onError={() => handleImageError(user?.id || '')}
            />
            <div className="flex-1">
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="What's on your mind?"
                className="w-full p-3 border dark:border-dark-border rounded-lg resize-none focus:ring-2 focus:ring-royal-purple focus:border-transparent min-h-[120px] transition-all duration-200 dark:bg-dark-elevated dark:text-dark-primary dark:placeholder-gray-500"
                style={{ height: content ? 'auto' : '120px' }}
              />
              
              {mediaPreview && (
                <div className="relative mt-3 rounded-lg overflow-hidden group">
                  {selectedMedia?.type.startsWith('video/') ? (
                    <video
                      src={mediaPreview}
                      controls
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  ) : (
                    <img
                      src={mediaPreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                      onError={() => setMediaPreview(null)}
                    />
                  )}
                  <button
                    onClick={() => {
                      setSelectedMedia(null);
                      setMediaPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t dark:border-dark-border bg-gray-50 dark:bg-dark-elevated">
          <div className="flex items-center gap-2">
            <label className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-paper cursor-pointer transition-colors group">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleMediaChange(e)}
                className="hidden"
              />
              <Image className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-royal-purple transition-colors" />
            </label>
            <label className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-paper cursor-pointer transition-colors group">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleMediaChange(e)}
                className="hidden"
              />
              <Video className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-royal-purple transition-colors" />
            </label>
          </div>
          <button
            onClick={handleSubmit}
            disabled={(!content.trim() && !selectedMedia) || isSubmitting}
            className={cn(
              'px-6 py-2 rounded-lg font-medium transition-all duration-200',
              (!content.trim() && !selectedMedia) || isSubmitting
                ? 'bg-gray-100 dark:bg-dark-paper text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'bg-royal-purple text-white hover:bg-royal-purple/90 hover:shadow-md shadow-royal-purple/25'
            )}
          >
            {isSubmitting ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>

      {posts.map((post) => (
        <div key={post.id} className="bg-white dark:bg-dark-paper rounded-xl shadow-sm hover:shadow-md transition-shadow">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <img
                  src={getAvatarUrl(post.user)}
                  alt={post.user?.full_name}
                  className="w-10 h-10 rounded-full"
                  onError={() => handleImageError(post.user?.id)}
                />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{post.user?.full_name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(post.created_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {user?.id === post.user_id ? (
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setShowReportDialog(post.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Flag className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{post.content}</p>

            {post.media_url && (
              post.media_type === 'video' ? (
                getVideoPlatform(post.media_url) ? (
                  <div className="relative pt-[56.25%] mt-4">
                    <iframe
                      src={post.media_url}
                      className="absolute inset-0 w-full h-full rounded-lg"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <video
                    src={post.media_url}
                    controls
                    className="mt-4 rounded-lg max-h-96 w-full"
                  />
                )
              ) : (
                <img
                  src={post.media_url}
                  alt="Post media"
                  className="mt-4 rounded-lg max-h-96 w-full object-cover"
                />
              )
            )}

            <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-4 mt-4 border-t dark:border-dark-border">
              <div className="relative">
                <button
                  onMouseEnter={() => setShowReactions(prev => ({ ...prev, [post.id]: true }))}
                  onClick={() => handleReaction(post.id, 'like')}
                  className={cn(
                    "flex items-center gap-2 transition-colors rounded-lg px-3 py-1.5",
                    post.user_reaction?.length
                      ? "text-royal-purple bg-royal-purple/10"
                      : "text-gray-600 dark:text-gray-400 hover:text-royal-purple hover:bg-gray-100 dark:hover:bg-dark-elevated"
                  )}
                >
                  {post.user_reaction?.length ? (
                    <span className="text-xl">
                      {REACTIONS.find(r => r.type === post.user_reaction?.[0].type)?.emoji}
                    </span>
                  ) : (
                    <Heart className="w-5 h-5" />
                  )}
                  <span>
                    {Object.values(post.reaction_counts || {}).reduce((a, b) => a + b, 0)}
                  </span>
                </button>

                {showReactions[post.id] && (
                  <div
                    className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-paper rounded-full shadow-lg border dark:border-dark-border p-2 flex gap-1 animate-fade-in z-10"
                    onMouseLeave={() => setShowReactions(prev => ({ ...prev, [post.id]: false }))}
                  >
                    {REACTIONS.map((reaction, index) => (
                      <button
                        key={reaction.type}
                        onClick={() => handleReaction(post.id, reaction.type)}
                        className={cn(
                          "p-2 rounded-full hover:bg-gray-100 dark:hover:bg-dark-elevated transition-all duration-200 group relative",
                          `animation-delay-${(index + 1)}00`
                        )}
                      >
                        <span className="block transform transition-all duration-200 group-hover:scale-125">
                          {reaction.emoji}
                        </span>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs font-medium text-white dark:text-dark-primary bg-gray-800 dark:bg-dark-elevated rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {reaction.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-royal-purple transition-colors rounded-lg px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-dark-elevated"
              >
                <MessageCircle className="w-5 h-5" />
                <span>{post.comments?.length || 0}</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowShare(prev => ({
                    ...prev,
                    [post.id]: !prev[post.id]
                  }))}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-royal-purple transition-colors rounded-lg px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-dark-elevated"
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share</span>
                </button>

                {showShare[post.id] && (
                  <ShareMenu
                    post={post}
                    onClose={() => setShowShare(prev => ({ ...prev, [post.id]: false }))}
                  />
                )}
              </div>
            </div>
          </div>

          {showComments[post.id] && (
            <div className="px-4 sm:px-6 pb-6">
              <CommentSection
                postId={post.id}
                comments={post.comments || []}
                onCommentUpdate={fetchPosts}
              />
            </div>
          )}
        </div>
      ))}

      {showReportDialog && (
        <ReportDialog
          postId={showReportDialog}
          onClose={() => setShowReportDialog(null)}
        />
      )}
    </div>
  );
}